import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './lib/auth'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Buildings from './pages/Buildings'
import Tenants from './pages/Tenants'
import Vehicles from './pages/Vehicles'
import AccessLogs from './pages/AccessLogs'
import Settings from './pages/Settings'
import Login from './pages/Login'
import AdminLogin from './pages/AdminLogin'
import SetupPassword from './pages/SetupPassword'
import ResetPassword from './pages/ResetPassword'
import ResidentDashboard from './pages/ResidentDashboard'
import ResidentProfile from './pages/ResidentProfile'
import ResidentVehicles from './pages/ResidentVehicles'
import ResidentAccess from './pages/ResidentAccess'
import ResidentSecurity from './pages/ResidentSecurity'

// Protected route wrapper using auth context
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

// Smart login route: admin subdomain → AdminLogin, otherwise → resident Login
function SmartLogin() {
  const subdomain = window.location.hostname.split('.')[0]
  return subdomain === 'admin' ? <AdminLogin /> : <Login />
}

// Resident routes — no admin sidebar, only personal pages
function ResidentRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ResidentDashboard />} />
      <Route path="/profile" element={<ResidentProfile />} />
      <Route path="/my-vehicles" element={<ResidentVehicles />} />
      <Route path="/access" element={<ResidentAccess />} />
      <Route path="/security" element={<ResidentSecurity />} />
      {/* Catch everything else and send back to resident home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

// Admin routes — full sidebar layout with all management pages
function AdminRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/buildings" element={<Buildings />} />
        <Route path="/tenants" element={<Tenants />} />
        <Route path="/residents" element={<Tenants />} />
        <Route path="/vehicles" element={<Vehicles />} />
        <Route path="/access-logs" element={<AccessLogs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  )
}

// Role-aware portal selector — uses user's ROLE to determine UI, not just subdomain
function PortalRouter() {
  const { showAdminUI, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  // After login: admin roles (super_admin, admin, tenant_admin, manager, staff) → AdminRoutes
  // Resident role → ResidentRoutes
  return showAdminUI ? <AdminRoutes /> : <ResidentRoutes />
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<SmartLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/setup-password" element={<SetupPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected routes — user's ROLE determines what they see */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <PortalRouter />
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default App
